import { api } from '@goaltree/backend/convex/_generated/api'
import type { PlaceSuggestion } from '@goaltree/backend/convex/lib/places'
import { cn } from '@goaltree/ui/lib/utils'
import { useAction, useQuery } from 'convex/react'
import { History, MapPin } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

export type PlaceCoords = { lat: number; lon: number }

type Option = { key: string; text: string; sub?: string; value: string; coords?: PlaceCoords }

const DEBOUNCE_MS = 300

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return v
}

// Place text field with your recent places and OpenStreetMap suggestions.
// Enter without picking keeps the typed text; Esc closes the list, then cancels.
export function PlaceInput({
  defaultValue = '',
  onCommit,
  onCancel,
  onEnter,
  placeholder,
  ariaLabel,
  className,
  autoFocus = true,
}: {
  defaultValue?: string
  onCommit: (text: string, coords?: PlaceCoords) => void
  onCancel: () => void
  onEnter?: () => void
  placeholder?: string
  ariaLabel?: string
  className?: string
  autoFocus?: boolean
}) {
  const { t, i18n } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const done = useRef(false)
  const [text, setText] = useState(defaultValue)
  const [open, setOpen] = useState(true)
  const [active, setActive] = useState(-1)
  const [host, setHost] = useState<HTMLElement | null>(null)
  const typed = text.trim()
  const q = useDebounced(typed, DEBOUNCE_MS)
  const recent = useQuery(api.places.recent, open ? { q: typed } : 'skip')
  const search = useAction(api.places.search)
  const [found, setFound] = useState<{ q: string; results: PlaceSuggestion[] } | null>(null)

  useEffect(() => {
    if (q.length < 2) return
    let live = true
    search({ q, lang: i18n.language })
      .then((results) => live && setFound({ q, results }))
      .catch(() => live && setFound({ q, results: [] }))
    return () => {
      live = false
    }
  }, [q, i18n.language, search])

  const searching = typed.length >= 2 && found?.q !== typed
  const recentOpts: Option[] = (recent ?? [])
    .filter((r) => r.text !== typed)
    .map((r) => ({
      key: `r:${r.text}`,
      text: r.text,
      value: r.text,
      ...(r.lat !== undefined && r.lon !== undefined && { coords: { lat: r.lat, lon: r.lon } }),
    }))
  const seen = new Set(recentOpts.map((o) => o.value.toLowerCase()))
  const searchOpts: Option[] =
    typed.length >= 2 && found?.q === typed
      ? found.results
          .filter((s) => !seen.has(s.label.toLowerCase()))
          .map((s) => ({
            key: `s:${s.lat},${s.lon}:${s.name}`,
            text: s.name,
            sub: s.address,
            value: s.label,
            coords: { lat: s.lat, lon: s.lon },
          }))
      : []
  const options = [...recentOpts, ...searchOpts]
  const show = open && (options.length > 0 || searching)

  useEffect(() => setActive(-1), [typed])

  useEffect(() => {
    const el = inputRef.current
    setHost((el?.closest('[role="dialog"]') as HTMLElement | null) ?? document.body)
  }, [])

  // Fixed to the input; corrects for a transformed host (sheets) shifting fixed boxes.
  useLayoutEffect(() => {
    if (!show) return
    const place = () => {
      const input = inputRef.current
      const list = listRef.current
      if (!input || !list) return
      const r = input.getBoundingClientRect()
      list.style.top = '0px'
      list.style.left = '0px'
      const off = list.getBoundingClientRect()
      const width = Math.max(r.width, 240)
      const left = Math.min(r.left, window.innerWidth - width - 8)
      list.style.width = `${width}px`
      list.style.top = `${r.bottom + 4 - off.top}px`
      list.style.left = `${Math.max(8, left) - off.left}px`
    }
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [show, options.length, searching])

  const commit = (value: string, coords?: PlaceCoords) => {
    if (done.current) return
    done.current = true
    onCommit(value.trim(), coords)
  }
  const pick = (o: Option) => {
    setText(o.value)
    commit(o.value, o.coords)
    onEnter?.()
  }

  const row = (o: Option, i: number) => (
    <button
      key={o.key}
      id={`place-opt-${i}`}
      type="button"
      role="option"
      aria-selected={i === active}
      onMouseEnter={() => setActive(i)}
      onClick={() => pick(o)}
      className={cn(
        'flex w-full cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-left',
        i === active && 'bg-muted',
      )}
    >
      {o.key.startsWith('r:') ? (
        <History className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
      ) : (
        <MapPin className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-foreground">{o.text}</span>
        {o.sub && <span className="block truncate text-muted-foreground">{o.sub}</span>}
      </span>
    </button>
  )

  const heading = (label: string) => (
    <div className="px-2 pt-1.5 pb-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </div>
  )

  return (
    <>
      <input
        ref={inputRef}
        autoFocus={autoFocus}
        value={text}
        maxLength={200}
        role="combobox"
        aria-expanded={show}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `place-opt-${active}` : undefined}
        aria-label={ariaLabel}
        placeholder={placeholder}
        onChange={(e) => {
          setText(e.target.value)
          setOpen(true)
        }}
        onBlur={() => commit(text)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && options.length) {
            e.preventDefault()
            setOpen(true)
            setActive((a) => (a + 1) % options.length)
          } else if (e.key === 'ArrowUp' && options.length) {
            e.preventDefault()
            setActive((a) => (a <= 0 ? options.length - 1 : a - 1))
          } else if (e.key === 'Enter') {
            e.preventDefault()
            if (show && active >= 0 && options[active]) pick(options[active])
            else {
              commit(text)
              onEnter?.()
            }
          } else if (e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
            if (show) setOpen(false)
            else {
              done.current = true
              onCancel()
            }
          }
        }}
        className={className}
      />
      {show &&
        host &&
        createPortal(
          <div
            ref={listRef}
            role="listbox"
            onMouseDown={(e) => e.preventDefault()}
            className="pointer-events-auto fixed z-[200] max-h-72 overflow-y-auto rounded-md bg-popover p-1 text-xs text-popover-foreground shadow-md ring-1 ring-foreground/10"
          >
            {recentOpts.length > 0 && heading(t('places.recent'))}
            {recentOpts.map((o, i) => row(o, i))}
            {(searchOpts.length > 0 || searching) && heading(t('places.suggestions'))}
            {searchOpts.map((o, i) => row(o, recentOpts.length + i))}
            {searching && searchOpts.length === 0 && (
              <div className="px-2 py-1.5 text-muted-foreground">{t('places.searching')}</div>
            )}
            {searchOpts.length > 0 && (
              <div className="px-2 pt-1 pb-0.5 text-right text-[10px] text-muted-foreground/70">
                © OpenStreetMap
              </div>
            )}
          </div>,
          host,
        )}
    </>
  )
}
